import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import { orderBy } from 'lodash'
import { API, graphqlOperation } from 'aws-amplify'
import * as queries from '../../graphql/queries'
import * as mutations from '../../graphql/mutations'
import { makeErrorToast, msg, makeSuccessToast } from '../../utils/makeToast'
import { fetchContactsByTenantIdUtil } from '@src/utils/fetchUtils'
import i18next from 'i18next'

// Fetches contacts that has users tenantId and the active flag is true
export const fetchContacts = createAsyncThunk(
  'contacts/fetchContacts',
  async (arg, { getState, rejectWithValue }) => {
    const { auth, settings } = getState()
    try {
      //   const sort = settings.sort
      // const {
      //   data: {
      //     contactsByTenantId: { items },
      //   },
      // } = await API.graphql(
      //   graphqlOperation(queries.contactsByTenantId, {
      //     tenantId: auth.userInfo.tenantId,
      //     filter: { activeFlag: { eq: true } },
      //   })
      // )
      const contactsData = await fetchContactsByTenantIdUtil(auth.userInfo.tenantId)

      // For any data fetched by datastore we need to stringify & then parse it for it to be serializable and used by redux
      // const parsedClientsData = JSON.parse(JSON.stringify(clientsData))
      //   let sortedClients
      //   if (sort === 'date') {
      //     sortedClients = orderBy(items, ['createdOn'], ['asc'])
      //   } else if (sort === 'name') {
      //     sortedClients = orderBy(items, [client => client.name.toLowerCase()], ['asc'])
      //   }
      return contactsData
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const getContactDetails = createAsyncThunk(
  'contacts/getContactDetails',
  async (contactId, { getState, rejectWithValue }) => {
    const { auth, settings } = getState()
    try {
      const {
        data: { getContact: items },
      } = await API.graphql(
        graphqlOperation(queries.getContact, {
          id: contactId,
        })
      )

      return items

      // For any data fetched by datastore we need to stringify & then parse it for it to be serializable and used by redux
      // const parsedClientsData = JSON.parse(JSON.stringify(clientsData))
      //   let sortedClients
      //   if (sort === 'date') {
      //     sortedClients = orderBy(items, ['createdOn'], ['asc'])
      //   } else if (sort === 'name') {
      //     sortedClients = orderBy(items, [client => client.name.toLowerCase()], ['asc'])
      //   }
      //   return items
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

// Creates a new contact item with info provided by user and then fetches contacts again
export const createContact = createAsyncThunk(
  'clients/createContact',
  async (contactInfo, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState()
      let newContact = {
        id: nanoid(),
        tenantId: auth.userInfo.tenantId,
        activeFlag: true,
        ...contactInfo,
      }
      // delete newContact['']
      // console.log(newContact)

      await API.graphql(graphqlOperation(mutations.createContact, { input: newContact }))
      makeSuccessToast(i18next.t('contacts:toasts.addedContact'))
      dispatch(fetchContacts())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

// Updates the contacts & fetches the contacts again
export const updateContact = createAsyncThunk(
  'clients/updateContact',
  async ({ contactId, newContact }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getContact },
      } = await API.graphql(graphqlOperation(queries.getContact, { id: contactId }))

      for (const property in newContact) {
        // if (property === '') continue
        getContact[property] = newContact[property]
      }
      delete getContact._deleted
      delete getContact.createdOn
      delete getContact.updatedOn
      delete getContact.invoices
      delete getContact.line02Bank
      delete getContact.cityBank
      delete getContact.stateBank
      delete getContact.postalCodeBank
      delete getContact.countryBank
      delete getContact._lastChangedAt

      await API.graphql(graphqlOperation(mutations.updateContact, { input: getContact }))
      makeSuccessToast(i18next.t('contacts:toasts.updatedContact'))
      dispatch(fetchContacts())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

// Deletes the contacts from id & fetches the contacts again
export const deleteContact = createAsyncThunk(
  'clients/deleteContact',
  async ({ contactInfo }, { dispatch, rejectWithValue }) => {
    try {
      await API.graphql(
        graphqlOperation(mutations.updateContact, {
          input: { id: contactInfo.id, _version: contactInfo._version, activeFlag: false },
        })
      )
      makeSuccessToast(i18next.t('contacts:toasts.deletedContact'))
      dispatch(fetchContacts())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const contactsSlice = createSlice({
  name: 'contacts',
  initialState: {
    contactsData: [],
    fetchContactsApiStatus: 'idle',
    createOrUpdateContactApiStatus: 'idle',
    viewContactStatus: 'idle',
    deleteContactApiStatus: 'idle',
    createContactModalOpen: false,
    viewContactOpen: false,
    contactDeleteDialogOpenStatus: false,
    currentContact: null,
    contactEditModalStatus: { isOpen: false },
    editContactMenuAnchorEl: null,
    error: null,
  },
  reducers: {
    setCreateContactModalOpen: (state, action) => {
      state.createContactModalOpen = action.payload.status
    },
    setViewContactOpen: (state, action) => {
      state.viewContactOpen = action.payload.status
      if (!action.payload.status) {
        state.currentContact = null
      }
    },
    setContactDeleteDialogOpenStatus: (state, action) => {
      state.contactDeleteDialogOpenStatus = action.payload.status
      if (!action.payload.status) {
        state.currentContact = null
      }
    },
    setEditContactMenuAnchorEl: (state, action) => {
      state.editContactMenuAnchorEl = action.payload
    },
    setContactEditModalStatus: (state, action) => {
      state.contactEditModalStatus = action.payload
    },
  },
  extraReducers: {
    [fetchContacts.pending]: (state, action) => {
      state.fetchContactsApiStatus = 'loading'
    },
    [fetchContacts.fulfilled]: (state, action) => {
      state.fetchContactsApiStatus = 'succeeded'
      state.contactsData = action.payload
    },
    [fetchContacts.rejected]: (state, action) => {
      state.fetchContactsApiStatus = 'failed'
      state.error = action.error.message
    },
    [createContact.pending]: (state, action) => {
      state.createOrUpdateContactApiStatus = 'loading'
    },
    [createContact.fulfilled]: (state, action) => {
      state.createOrUpdateContactApiStatus = 'succeeded'
      state.createContactModalOpen = false
    },
    [createContact.rejected]: (state, action) => {
      state.createOrUpdateClientApiStatus = 'failed'
      state.error = action.error.message
    },
    [getContactDetails.pending]: (state, action) => {
      state.viewContactStatus = 'loading'
    },
    [getContactDetails.fulfilled]: (state, action) => {
      state.viewContactStatus = 'succeeded'
      state.currentContact = action.payload
    },
    [getContactDetails.rejected]: (state, action) => {
      state.viewContactStatus = 'failed'
      state.currentContact = null
      state.error = action.error.message
    },
    [updateContact.pending]: (state, action) => {
      state.createOrUpdateContactApiStatus = 'loading'
    },
    [updateContact.fulfilled]: (state, action) => {
      state.createOrUpdateContactApiStatus = 'succeeded'
      state.contactEditModalStatus = false
      state.currentContact = null
    },
    [updateContact.rejected]: (state, action) => {
      state.createOrUpdateContactApiStatus = 'failed'
      state.contactEditModalStatus = false
      state.error = action.error.message
    },
    [deleteContact.pending]: (state, action) => {
      state.deleteContactApiStatus = 'loading'
    },
    [deleteContact.fulfilled]: (state, action) => {
      state.deleteContactApiStatus = 'succeeded'
      state.contactDeleteDialogOpenStatus = false
    },
    [deleteContact.rejected]: (state, action) => {
      state.deleteContactApiStatus = 'failed'
      state.contactDeleteDialogOpenStatus = false
      state.error = action.error.message
    },
  },
})

export const {
  setCreateContactModalOpen,
  setViewContactOpen,
  setContactDeleteDialogOpenStatus,
  setEditContactMenuAnchorEl,
  setContactEditModalStatus,
} = contactsSlice.actions

export default contactsSlice.reducer
